import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const generateSmoothPath = (points, width, height, padding = 0) => {
  if (points.length < 2) return "";
  const stepX = (width - padding * 2) / (points.length - 1);
  const coords = points.map((p, i) => ({
    x: padding + i * stepX,
    y: height - padding - (p / 100) * (height - padding * 2),
  }));

  let d = `M ${coords[0].x} ${coords[0].y}`;
  for (let i = 1; i < coords.length; i++) {
    const prev = coords[i - 1];
    const curr = coords[i];
    const cpx1 = prev.x + stepX * 0.4;
    const cpx2 = curr.x - stepX * 0.4;
    d += ` C ${cpx1} ${prev.y}, ${cpx2} ${curr.y}, ${curr.x} ${curr.y}`;
  }
  return d;
};

const generateAreaPath = (points, width, height, padding = 0) => {
  const linePath = generateSmoothPath(points, width, height, padding);
  const stepX = (width - padding * 2) / (points.length - 1);
  const lastX = padding + (points.length - 1) * stepX;
  return `${linePath} L ${lastX} ${height - padding} L ${padding} ${height - padding} Z`;
};

const brandData = [32, 28, 42, 38, 52, 45, 58, 55, 65, 50, 68, 62, 72, 65, 78, 72, 82, 75, 70, 78, 65, 72, 60, 70, 52, 65, 48, 62, 58, 75];
const compData = [25, 22, 35, 30, 40, 38, 48, 42, 52, 45, 55, 50, 58, 52, 62, 56, 60, 55, 52, 58, 50, 56, 48, 55, 42, 50, 38, 48, 45, 55];

export function AnimatedChart() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });
  const W = 700;
  const H = 160;
  const P = 8;

  const brandPath = generateSmoothPath(brandData, W, H, P);
  const brandArea = generateAreaPath(brandData, W, H, P);
  const compPath = generateSmoothPath(compData, W, H, P);
  const compArea = generateAreaPath(compData, W, H, P);

  return (
    <div ref={ref} className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-mono text-muted-foreground">Competitor Ad Activity - Last 30 Days</p>
        <div className="flex gap-4">
          <span className="flex items-center gap-1.5 text-[10px] text-lime-400">
            <span className="h-1.5 w-3 rounded-full bg-lime-500" /> Your brand
          </span>
          <span className="flex items-center gap-1.5 text-[10px] text-[#7AD7FF]">
            <span className="h-1.5 w-3 rounded-full bg-[#7AD7FF]" /> Competitor avg
          </span>
        </div>
      </div>
      <div className="relative w-full" style={{ aspectRatio: `${W}/${H}` }}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="none">
          {[0.25, 0.5, 0.75].map((pct, i) => (
            <line
              key={i}
              x1={P}
              y1={H - P - pct * (H - P * 2)}
              x2={W - P}
              y2={H - P - pct * (H - P * 2)}
              stroke="rgba(255,255,255,0.04)"
              strokeWidth="0.5"
              strokeDasharray="4 4"
            />
          ))}
          <motion.path d={compArea} fill="url(#compGradient)" initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : { opacity: 0 }} transition={{ duration: 0.8, delay: 0.6 }} />
          <motion.path d={brandArea} fill="url(#brandGradient)" initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : { opacity: 0 }} transition={{ duration: 0.8, delay: 0.3 }} />
          <motion.path d={compPath} fill="none" stroke="#7AD7FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0, opacity: 0 }} animate={isInView ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }} transition={{ duration: 1.4, delay: 0.4, ease: [0.22, 1, 0.36, 1] }} />
          <motion.path d={brandPath} fill="none" stroke="#CCFF6C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0, opacity: 0 }} animate={isInView ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }} transition={{ duration: 1.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }} />
          <defs>
            <linearGradient id="brandGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#CCFF6C" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#CCFF6C" stopOpacity="0.01" />
            </linearGradient>
            <linearGradient id="compGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7AD7FF" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#7AD7FF" stopOpacity="0.01" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
}
